import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  User,
  LoginCredentials,
  CreateUserDto,
  TwoFactorVerification,
  UpdateOwnProfileDto,
} from "@autfit/shared-types";
import { authAPI, usersAPI } from "../../services/api";
import { clearSessionRecord } from "../../services/sessionRefresh";
import {
  clearAllDrafts,
  clearDraftsOfOtherUsers,
} from "../../utils/resultDrafts";

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  requiresTwoFactor: boolean;
  tempUserId: string | null;
  error: string | null;
  /** The first auth check after app start has finished. */
  initialized: boolean;
  /**
   * The refresh failed while logged in: the re-login dialog is shown over
   * the mounted page. `user` is kept until the re-login.
   */
  sessionExpired: boolean;
}

const initialState: AuthState = {
  user: null,
  isLoading: true, // Start with loading true to check auth status on app load
  isAuthenticated: false,
  requiresTwoFactor: false,
  tempUserId: null,
  error: null,
  initialized: false,
  sessionExpired: false,
};

/** Local cleanup on explicit logout (drafts of all users, refresh record). */
const clearLocalSessionData = () => {
  clearAllDrafts();
  clearSessionRecord();
};

// Async thunks
export const checkAuthStatus = createAsyncThunk(
  "auth/checkAuthStatus",
  async (_, { rejectWithValue }) => {
    try {
      const user = await authAPI.getMe();
      clearDraftsOfOtherUsers(user.id);
      return user;
    } catch (error: any) {
      // Don't treat 401 as an error - user is just not authenticated
      // Also don't log or report 401 errors as they're expected when no valid tokens exist
      if (error.response?.status === 401) {
        return rejectWithValue(null);
      }
      // Only log non-401 errors as they might indicate actual problems
      console.error("Auth status check failed:", error);
      return rejectWithValue(
        error.response?.data?.message || "Authentication failed",
      );
    }
  },
);

export const login = createAsyncThunk(
  "auth/login",
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      if (!response.requiresTwoFactor)
        clearDraftsOfOtherUsers(response.user.id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  },
);

export const register = createAsyncThunk(
  "auth/register",
  async (userData: CreateUserDto, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(userData);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Registration failed",
      );
    }
  },
);

export const verify2FA = createAsyncThunk(
  "auth/verify2FA",
  async (
    {
      userId,
      verification,
    }: { userId: string; verification: TwoFactorVerification },
    { rejectWithValue },
  ) => {
    try {
      const response = await authAPI.verify2FA(userId, verification);
      clearDraftsOfOtherUsers(response.user.id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "2FA verification failed",
      );
    }
  },
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.logout();
    } catch (error: any) {
      // Continue with logout even if API call fails
      console.warn("Logout API call failed, continuing with local logout");
    } finally {
      clearLocalSessionData();
    }
  },
);

export const logoutAll = createAsyncThunk(
  "auth/logoutAll",
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.logoutAll();
    } catch (error: any) {
      // Continue with logout even if API call fails
      console.warn("Logout all API call failed, continuing with local logout");
    } finally {
      clearLocalSessionData();
    }
  },
);

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (data: UpdateOwnProfileDto, { rejectWithValue }) => {
    try {
      // Own profile only: PATCH /users/me (every logged-in user).
      const updatedUser = await usersAPI.updateMe(data);
      return updatedUser;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Profile update failed",
      );
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    reset2FA: (state) => {
      state.requiresTwoFactor = false;
      state.tempUserId = null;
    },
    /** A refresh failed with 401 (services/sessionEvents.ts). */
    sessionExpired: (state) => {
      if (!state.isAuthenticated || !state.user) return;
      state.isAuthenticated = false;
      state.sessionExpired = true;
    },
    /** Re-login from the dialog succeeded (same or another user). */
    reloginSucceeded: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.sessionExpired = false;
      state.requiresTwoFactor = false;
      state.tempUserId = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check auth status
      .addCase(checkAuthStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.initialized = true;
        state.sessionExpired = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.initialized = true;
        state.sessionExpired = false;
        state.user = null;
        state.isAuthenticated = false;
        // Only set error if it's not a 401 (null payload)
        state.error = action.payload ? (action.payload as string) : null;
      })

      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.requiresTwoFactor) {
          state.requiresTwoFactor = true;
          state.tempUserId = action.payload.user.id;
        } else {
          state.user = action.payload.user;
          state.isAuthenticated = true;
          state.sessionExpired = false;
        }
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Verify 2FA
      .addCase(verify2FA.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verify2FA.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.sessionExpired = false;
        state.requiresTwoFactor = false;
        state.tempUserId = null;
        state.error = null;
      })
      .addCase(verify2FA.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.requiresTwoFactor = false;
        state.tempUserId = null;
        state.error = null;
        state.isLoading = false;
        state.sessionExpired = false;
      })

      // Logout All
      .addCase(logoutAll.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.requiresTwoFactor = false;
        state.tempUserId = null;
        state.error = null;
        state.isLoading = false;
        state.sessionExpired = false;
      })

      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, reset2FA, sessionExpired, reloginSucceeded } =
  authSlice.actions;
export default authSlice.reducer;
